import React, { useEffect, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
// scss
import './scss/sensitive-record.style.scss';
import { SensitiveRecordState } from './sensitive-operations-state';
import i18n from '../../i18n';
import CardView from '../../component/card/card-view';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import { Box, Grid } from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import PeriodDateFilter from '../../component/period-date-filter.component';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import moment from 'moment';
import { OperationType, SensitiveRecord } from '../../models/sensitive-record.entity';
import { useTranslation } from 'react-i18next';
import { ConditionType, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import { toJS } from 'mobx';
import ButtonComponent from '../../component/button/button.component';
import SensitiveOperationsDetail from './sensitive-operations-detail';
import { accountState } from '../my-account/account-state';
import { Staff } from '../../models';

/**
 * 将名称与电话格式化为「名称/电话」；无电话时仅返回名称。
 * @param name - 显示名称
 * @param phonePrefix - 电话区号
 * @param phone - 电话号码
 */
function formatNameWithPhone(name?: string, phonePrefix?: string, phone?: string): string {
    const trimmedName = (name ?? '').trim();
    const trimmedPhone = [phonePrefix, phone].filter(Boolean).join(' ').trim();
    if (!trimmedName && !trimmedPhone) {
        return '';
    }
    if (!trimmedPhone) {
        return trimmedName;
    }
    if (!trimmedName) {
        return trimmedPhone;
    }
    return `${trimmedName}/${trimmedPhone}`;
}

/**
 * 从敏感操作记录中解析关联员工信息（按操作类型取 details 内不同字段）。
 * @param row - 敏感操作记录行
 */
function getStaffFromRecord(row: SensitiveRecord): Staff | undefined {
    const details = row.details;
    if (!details) {
        return undefined;
    }
    return details.staff_info ?? details.staff ?? details.new_staff_info ?? details.origin_staff_info;
}

/**
 * 取员工展示名称（姓+名，缺省时用 nickname）。
 * @param staff - 员工实体
 */
function getStaffDisplayName(staff?: Staff): string {
    if (!staff) {
        return '';
    }
    const fullName = `${staff.first_name ?? ''} ${staff.last_name ?? ''}`.trim();
    return fullName || (staff.nickname ?? '').trim();
}

/**
 * 敏感操作记录页面
 */
const SensitiveOperations = observer(() => {

    useTranslation();

    // 使用数据管理
    const sensitiveRecordData = useLocalObservable(() => new SensitiveRecordState());
    // 员工详情弹窗
    const [isOpenDetail, setIsOpenDetail] = useState<boolean>(false);
    const [detail, setDetail] = useState<SensitiveRecord>({});

    const {
        selectValue,
        opTypeSelectValue,
        sensitiveRecordList,
        isTableLoading,
        conditions,
        dataTotal,
        viewDateTimeData
    } = sensitiveRecordData;

    const SearchKeyInputRef = React.useRef<{ setSelectKey: (val: string) => void; }>();

    useEffect(() => {
        sensitiveRecordData.init();
        const topbarState = MobxObjectsManager.get(TopBarState);
        topbarState.updateOperateRecordTitle();
        return () => {
            MobxObjectsManager.remove(SensitiveRecordState);
        }
    }, []);

    /**
     * 打开员工详情弹窗
     * @param row
     */
    const handleOpenDetail = async (row: SensitiveRecord) => {
        setIsOpenDetail(true);
        setDetail(row)
    }

    /**
     * 关闭详情弹窗
     */
    const handleClose = () => {
        setIsOpenDetail(false);
    }


    // 表格操作类型下拉框 静态数据
    let operationTypeList: { value: string; label: string }[] = [
        {
            value: OperationType.OP_ADD_WARRANT,
            label: i18n.t('operation_record_addition_authorization')
        }, {
            value: OperationType.OP_CANCEL_WARRANT,
            label: i18n.t('operation_record_delete_authorization')
        }, {
            value: OperationType.OP_Add_GybDevice,
            label: i18n.t('operation_record_add_a_GYB_device')
        }, {
            value: OperationType.OP_Delete_GybDevice,
            label: i18n.t('operation_record_delete_a_GYB_device')
        }, {
            value: OperationType.OP_Default_GybDevice,
            label: i18n.t('operation_record_GYB_sets_default')
        }, {
            value: OperationType.OP_Add_SecretKey,
            label: i18n.t('operation_record_add_function_key')
        }, {
            value: OperationType.OP_Refresh_SecretKey,
            label: i18n.t('operation_record_refresh_function_key')
        }, {
            value: OperationType.OP_Delete_SecretKey,
            label: i18n.t('operation_record_delete_function_key')
        }, {
            value: OperationType.OP_Replace_Group,
            label: i18n.t('operation_record_replace_group')
        }, {
            value: OperationType.OP_Delete_Staff,
            label: i18n.t('sensitive_operations_page_text_delete_staff')
        }, {
            value: OperationType.OP_Add_Staff,
            label: i18n.t('ground_staff_component_text_new_create_employee')
        }, {
            value: OperationType.OP_Update_Staff,
            label: i18n.t('ground_staff_component_text_edit_staff_account')
        }, {
            value: OperationType.OP_Bind_Restaurant,
            label: i18n.t('ground_staff_component_text_associated_restaurant')
        }, {
            value: OperationType.OP_Unbind_Restaurant,
            label: i18n.t('sensitive_operations_page_text_restaurant_unbind_staff')
        }, {
            value: OperationType.OP_Update_Password,
            label: i18n.t('general_text_modify_password')
        }, {
            value: OperationType.OP_Add_Reseller,
            label: i18n.t('general_text_add_reseller')
        }, {
            value: OperationType.OP_Delete_Reseller,
            label: i18n.t('general_text_delete_reseller')
        }, {
            value: OperationType.OP_Update_Cloud_Analysis_Origin,
            label: i18n.t('sensitive_operation_update_cloud_analysis_origin')
        }
    ];
    operationTypeList = accountState.accountType === 'ADMIN' ? operationTypeList : operationTypeList.filter(item => !item.value.includes('Reseller'))

    // 查找表格条件下拉框 静态数据
    const filterList: SearchKeyInputItems[] = [
        {
            value: 'admin_nickname',
            label: i18n.t('operation_record_operator'),
            type: 'TEXT'
        }, {
            value: 'belonged_group_nickname',
            label: i18n.t('operation_record_group_nickname'),
            type: 'TEXT'
        }, {
            value: 'belonged_restaurant_nickname',
            label: i18n.t('operation_record_restaurant_nickname'),
            type: 'TEXT'
        }, {
            value: 'operation_type',
            label: i18n.t('operation_record_operation_type'),
            type: 'SELECT',
            options: operationTypeList
        }, {
            value: 'name',
            label: i18n.t('operation_record_the_authorization_of_operations'),
            type: 'TEXT'
        }
    ];


    // 表格头部
    const tableHeadCell: HeadCell[] = [
        {
            id: 'admin_nickname',
            label: i18n.t('operation_record_operator'),
            sortStatus: -1,
            renderFunction: (row: SensitiveRecord) => {
                const operator = row?.operator?.[0];
                const operatorName = row.admin_nickname ?? operator?.nickname ?? '';
                const phonePrefix = operator?.phone_prefix ?? row.phone_prefix;
                const phone = operator?.phone ?? row.phone;
                return <>{formatNameWithPhone(operatorName, phonePrefix, phone)}</>;
            },
        },
        {
            id: 'belonged_group', label: i18n.t('group_management_page_text_group'),
            renderFunction: (row: SensitiveRecord) => {
                return <>{row?.belonged_group?.[0]?.nickname ?? ''}</>
            }
        },
        {
            id: 'belonged_restaurant', label: i18n.t('group_management_page_text_restaurant'),
            renderFunction: (row: SensitiveRecord) => {
                return row?.belonged_restaurant?.[0]?.nickname ?? ''
            }
        },
        {
            id: 'staff_name',
            label: i18n.t('sensitive_operations_page_col_staff'),
            renderFunction: (row: SensitiveRecord) => {
                const staff = getStaffFromRecord(row);
                return <>{formatNameWithPhone(getStaffDisplayName(staff), staff?.phone_prefix, staff?.phone)}</>;
            },
        },
        {
            id: 'operation_type', label: i18n.t('operation_record_operation_type'),
            renderFunction: (row: SensitiveRecord) => {
                return <>{i18n.t(sensitiveRecordData.showOpType(row?.operation_type ?? '') ?? '')}</>
            }
        },
        {
            id: 'operation_time', label: i18n.t('operation_record_operation_time'), sortStatus: sensitiveRecordData.getSortStatus('operation_time'),
            renderFunction: (row: SensitiveRecord) => {
                // 操作时间
                const operationTime = moment(row?.operation_time).format('YYYY/MM/DD HH:mm:ss');
                return <>{operationTime}</>
            }
        },
        {
            id: 'machine_ip',
            label: 'ip'
        },
        {
            id: 'name', label: i18n.t('operation_record_operation_object_details'),
            renderFunction: (row) => {
                return (
                    <ButtonComponent action='VIEW' handleClick={() => handleOpenDetail(row)} />
                )
            }
        },
    ];

    // *********************
    // Extra Function
    // *********************

    // 渲染页面组件
    const renderContent = (): JSX.Element => {
        return (
            <Box className={'contain'}>
                <Grid container justifyContent='space-between'>
                    {/* </Grid> */}
                    <Grid item xs={6}>
                        <Box className='table-top-tool'>
                            <SearchKeyInput
                                ref={SearchKeyInputRef}
                                items={filterList}
                                conditions={toJS(conditions)}
                                onSearch={(conditions: ConditionType[]) => {
                                    sensitiveRecordData.setConditions(conditions);
                                }}
                                showConditionsBox
                            />
                            {/* 授权类型选择下拉框 */}
                            {
                                selectValue === 'operation_type' &&
                                <Box marginLeft={2}>
                                    <CommonInput
                                        inputType={'SELECT'}
                                        select={true}
                                        items={operationTypeList ?? []}
                                        size={'small'}
                                        fullWidth
                                        value={opTypeSelectValue}
                                        onChange={(e) => {
                                            const selectVal = e.target.value;
                                            sensitiveRecordData.setOpTypeSelectVal(selectVal);
                                        }}
                                    />
                                </Box>
                            }
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box className='table-top-tool'>
                            <PeriodDateFilter
                                startDate={sensitiveRecordData.startDate}
                                endDate={sensitiveRecordData.endDate}
                                pickerValue={[viewDateTimeData.startDate, viewDateTimeData.endDate]}
                                onDateTimeChange={(dates) => sensitiveRecordData.handleChangePicker(dates)}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <TableComponent
                            {...{
                                showChecked: 0,
                                list: sensitiveRecordList ?? [],
                                // 开启表格自定义页脚
                                isUseCustomPagination: true,
                                headCellList: tableHeadCell || [],
                                isTableLoading,
                                handleSort: (headCell) => {
                                    sensitiveRecordData._handleSort(headCell);
                                },
                                isPagination: true,
                                allCount: dataTotal,
                                isUseFold: true,
                                extraClass: 'extraTable',
                                changePageCallback: (params) => sensitiveRecordData.changePageCallback(params),
                                isCurrentPageData: true,
                                currentPage: sensitiveRecordData.page
                            }}
                        />
                    </Grid>
                </Grid>
                <SensitiveOperationsDetail
                    isOpen={isOpenDetail}
                    onClose={handleClose}
                    detail={detail}
                />
            </Box>
        )
    }

    // *********************
    // Render View
    // *********************

    return (
        <Box className={'sensitive-operation-record-page'}>
            <Box sx={{}}>
                <CardView
                    {...{
                        className: '',
                        contentRender: renderContent()
                    }}
                />
            </Box>
        </Box>
    )
});

export default SensitiveOperations;

